import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { getErrorMessage } from 'utils/utilities';
import useInterval from 'hooks/useInterval';
import { useTwoFactorAuthMutation, useVerifyTwoFactorAuthMutation } from 'features/onboarding';
import validationSchema from './OTPForm.validation';

const RETRY_OFFSET_SECONDS = 60;

export default function useOTPForm({ token, setVerified }) {
    const [counter, setCounter] = useState(0);
    const [sendOtp, { isLoading: sendLoading, isUninitialized }] = useTwoFactorAuthMutation();
    const [verifyOtp] = useVerifyTwoFactorAuthMutation();

    const loading = isUninitialized || sendLoading;

    const formik = useFormik({
        initialValues: { code: '' },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await verifyOtp({ code: values.code, token }).unwrap();
                setSubmitting(false);
                setVerified(true);
            } catch (error) {
                toast.error(error?.response?.data?.errorMessage || getErrorMessage(error));
                setSubmitting(false);
            }
        },
    });

    const resend = async () => {
        try {
            await sendOtp(token).unwrap();
            setCounter(0);
            formik.resetForm();
        } catch (error) {
            toast.error(getErrorMessage(error));
        }
    };

    useEffect(() => {
        if (!token) return;
        sendOtp(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useInterval(() => {
        if (counter < RETRY_OFFSET_SECONDS) {
            setCounter((prev) => prev + 1);
        }
    }, 1000);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        globalThis.addEventListener('beforeunload', handleBeforeUnload);
        globalThis.addEventListener('popstate', handleBeforeUnload);
        return () => {
            globalThis.removeEventListener('beforeunload', handleBeforeUnload);
            globalThis.removeEventListener('popstate', handleBeforeUnload);
        };
    }, []);

    const secondsLeft = useMemo(() => RETRY_OFFSET_SECONDS - counter, [counter]);
    const formattedSeconds = useMemo(
        () => (secondsLeft < 10 ? `0${secondsLeft}` : secondsLeft),
        [secondsLeft]
    );
    const canRetry = useMemo(() => secondsLeft <= 0, [secondsLeft]);

    return { formik, loading, canRetry, secondsLeft, formattedSeconds, resend };
}
